/**
 * Tools Page Component
 * Displays and manages tools/integrations from MCP servers
 * Phase 4 implementation
 */

import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '../components/layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Alert, AlertDescription } from '../components/ui/alert';
import { useToast } from '../hooks/use-toast';
import { externalToolsService } from '../services/external-tools';
import { mcpHostService } from '../services/mcp-host';
import { useAuthStore } from '../stores/authStore';
import { apiClient } from '../services/api';
import MCPConnectionForm from '../components/tools/MCPConnectionForm';
import {
  Search,
  Plus,
  Check,
  Trash2,
  Settings2,
  Loader2,
  AlertCircle,
  RefreshCw,
  Wrench,
  X
} from 'lucide-react';

interface MCPTool {
  id: string;
  mcp_server_name: string;
  server_url: string;
  transport_type: string;
  metadata: {
    description?: string;
    [key: string]: any;
  };
  status: 'hidden' | 'shown' | 'deprecated' | 'maintenance';
  icon?: string;
  selected?: boolean;
  enabled?: boolean;
  connectionStatus?: 'connected' | 'disconnected' | 'pending' | 'error';
}

const ExternalTools: React.FC = () => {
  const [availableTools, setAvailableTools] = useState<MCPTool[]>([]);
  const [userTools, setUserTools] = useState<MCPTool[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [configTool, setConfigTool] = useState<MCPTool | null>(null);
  const [connectionStatuses, setConnectionStatuses] = useState<Record<string, 'connected' | 'disconnected' | 'pending' | 'error'>>({});
  const { toast } = useToast();
  const { user } = useAuthStore();

  useEffect(() => {
    loadTools();
  }, []);

  useEffect(() => {
    // Check connection status for all user tools
    if (userTools.length > 0) {
      checkConnectionStatuses();
    }
  }, [userTools]);

  const loadTools = async () => {
    try {
      setLoading(true);

      // Use the existing external-tools service
      const allTools = await externalToolsService.getAvailableTools();
      setAvailableTools(allTools);

      // Fetch user's connected MCP servers
      if (user?.id) {
        const userTools = await externalToolsService.getUserTools(user.id);
        setUserTools(userTools);
      }
    } catch (error) {
      console.error('Failed to load MCP servers:', error);
      toast({
        title: 'Error',
        description: 'Failed to load MCP servers. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddTool = async (tool: MCPTool) => {
    try {
      if (!user?.id) {
        toast({
          title: 'Error',
          description: 'Please log in to add tools.',
          variant: 'destructive',
        });
        return;
      }

      await externalToolsService.addUserTool(user.id, tool.id);

      // Add to user tools with enabled flag
      setUserTools([...userTools, { ...tool, enabled: true }]);

      // Check if tool requires auth (OAuth, API key, etc.)
      const requiresAuth = tool.metadata?.auth_type || tool.metadata?.oauth_config;

      if (!requiresAuth) {
        // For tools without auth (like stdio OCR), auto-connect immediately
        try {
          toast({
            title: 'Connecting...',
            description: `Connecting to ${tool.mcp_server_name}...`,
          });

          const response = await apiClient.post('/api/mcp/connect', {
            tenant_id: user.id,
            mcp_server_id: tool.id
          });

          if (response.data?.success) {
            toast({
              title: 'Connected!',
              description: `${tool.mcp_server_name} is now ready to use.`,
            });
            // Refresh connection status
            await checkConnectionStatuses();
          } else {
            toast({
              title: 'Connection Failed',
              description: response.data?.message || 'Failed to connect. Try Configure.',
              variant: 'destructive',
            });
          }
        } catch (connectError) {
          console.error('Auto-connect failed:', connectError);
          toast({
            title: 'Auto-connect Failed',
            description: 'Tool added but connection failed. Click Configure to retry.',
            variant: 'destructive',
          });
        }
      } else {
        // For OAuth tools, user needs to configure
        toast({
          title: 'MCP Server Added',
          description: `${tool.mcp_server_name} has been added. Click Configure to authenticate.`,
        });
      }
    } catch (error) {
      console.error('Failed to add tool:', error);
      toast({
        title: 'Error',
        description: 'Failed to add tool. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleRemoveTool = async (toolId: string) => {
    try {
      if (!user?.id) {
        toast({
          title: 'Error',
          description: 'Please log in to remove tools.',
          variant: 'destructive',
        });
        return;
      }

      await externalToolsService.removeUserTool(user.id, toolId);

      // Remove from user tools
      setUserTools(userTools.filter(t => t.id !== toolId));
      
      toast({
        title: 'Tool Removed',
        description: 'Tool has been removed from your collection.',
      });
    } catch (error) {
      console.error('Failed to remove tool:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove tool. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const checkConnectionStatuses = async () => {
    if (!user?.id) return;
    
    const statuses: Record<string, 'connected' | 'disconnected' | 'pending' | 'error'> = {};
    
    // Check connection status for each user tool
    for (const tool of userTools) {
      try {
        const response = await apiClient.get('/api/mcp/status', {
          params: {
            tenant_id: user.id,
            mcp_server_id: tool.id
          }
        });
        
        if (response.data?.success && response.data?.data) {
          const status = response.data.data;
          if (status.connected && status.initialized) {
            statuses[tool.id] = 'connected';
          } else if (status.error) {
            statuses[tool.id] = 'error';
          } else {
            statuses[tool.id] = 'disconnected';
          }
        } else {
          statuses[tool.id] = 'disconnected';
        }
      } catch (error) {
        console.error(`Failed to check connection status for ${tool.mcp_server_name}:`, error);
        statuses[tool.id] = 'error';
      }
    }
    
    setConnectionStatuses(statuses);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadTools();
    await checkConnectionStatuses();
    setRefreshing(false);
    toast({
      title: 'Refreshed',
      description: 'Tools list has been updated.',
    });
  };

  const filteredTools = availableTools.filter(tool =>
    tool.mcp_server_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tool.metadata?.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const isToolSelected = (toolId: string) => {
    return userTools.some(t => t.id === toolId);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container mx-auto p-6 max-w-7xl">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">Tools</h1>
          <Button
            onClick={handleRefresh}
            disabled={refreshing}
            variant="outline"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Search Bar */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search tools by name or description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Tools Grid - Combined View */}
        <ScrollArea className="h-[600px]">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTools.map((tool) => {
              const selected = isToolSelected(tool.id);
              return (
                <Card 
                  key={tool.id}
                  className={`relative transition-all ${selected ? 'border-primary shadow-md' : ''}`}
                >
                  {/* Delete button in top-right corner for selected tools */}
                  {selected && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute top-2 right-2 h-8 w-8 hover:bg-destructive/10"
                      onClick={() => handleRemoveTool(tool.id)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                  
                  <CardHeader className="pb-3">
                    <div className="flex items-start space-x-2 pr-8">
                      {tool.icon ? (
                        <span className="text-2xl mt-0.5">{tool.icon}</span>
                      ) : (
                        <Wrench className="h-5 w-5 text-muted-foreground mt-0.5" />
                      )}
                      <div className="flex-1">
                        <CardTitle className="text-lg flex items-center gap-2">
                          {tool.mcp_server_name}
                          {selected && (
                            <Badge variant="secondary" className="text-xs">
                              <Check className="h-3 w-3 mr-1" />
                              Added
                            </Badge>
                          )}
                        </CardTitle>
                        <CardDescription className="mt-1">
                          {tool.metadata?.description || 'No description available'}
                        </CardDescription>
                        {/* Connection Status Indicator */}
                        {selected && connectionStatuses[tool.id] && (
                          <div className="flex items-center gap-2 mt-2">
                            <div className={`h-2 w-2 rounded-full ${
                              connectionStatuses[tool.id] === 'connected' ? 'bg-green-500' :
                              connectionStatuses[tool.id] === 'error' ? 'bg-red-500' :
                              connectionStatuses[tool.id] === 'pending' ? 'bg-yellow-500' :
                              'bg-gray-400'
                            }`} />
                            <span className="text-xs text-muted-foreground">
                              {connectionStatuses[tool.id] === 'connected' ? 'Connected' :
                               connectionStatuses[tool.id] === 'error' ? 'Connection Error' :
                               connectionStatuses[tool.id] === 'pending' ? 'Connecting...' :
                               'Not Connected'}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CardContent>
                    <div className="flex items-center justify-between">
                      {/* Left side - Configure button for selected tools that need auth */}
                      {selected ? (
                        <>
                          {(tool.metadata?.auth_type || tool.metadata?.oauth_config) ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                // Use dynamic configuration component for OAuth tools
                                setConfigTool(tool);
                              }}
                            >
                              <Settings2 className="h-4 w-4 mr-1" />
                              Configure
                            </Button>
                          ) : (
                            <div /> // No configure button for tools without auth
                          )}
                        </>
                      ) : (
                        <div /> // Empty div for spacing
                      )}
                      
                      {/* Right side - Add button for unselected tools */}
                      {!selected && (
                        <Button
                          variant="default"
                          size="sm"
                          className="ml-auto"
                          onClick={() => handleAddTool(tool)}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add Tool
                        </Button>
                      )}
                      
                      {/* Status badges */}
                      {selected && tool.enabled !== undefined && (
                        <Badge variant={tool.enabled ? 'default' : 'secondary'}>
                          {tool.enabled ? 'Active' : 'Inactive'}
                        </Badge>
                      )}
                    </div>
                    
                    {/* Maintenance indicator */}
                    {tool.status === 'maintenance' && (
                      <Badge variant="secondary" className="mt-2">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        Under Maintenance
                      </Badge>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
          
          {/* Empty state */}
          {filteredTools.length === 0 && (
            <Card className="mt-8">
              <CardContent className="text-center py-12">
                <Wrench className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No tools available</h3>
                <p className="text-muted-foreground">
                  {searchQuery 
                    ? 'No tools match your search criteria.' 
                    : 'Tools will appear here once they are configured in the system.'}
                </p>
              </CardContent>
            </Card>
          )}
        </ScrollArea>

        {/* Info Alert */}
        <Alert className="mt-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            These tools will be available in your AI chat sessions. Select tools that match your workflow needs.
          </AlertDescription>
        </Alert>

        {/* Dynamic Configuration Modal - Works for any MCP tool */}
        {configTool && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-background rounded-lg shadow-xl max-w-lg w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">
                  {configTool.mcp_server_name} Configuration
                </h2>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setConfigTool(null)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              
              {/* MCP Connection Form - follows MCP protocol standard */}
              <MCPConnectionForm 
                mcpServerId={configTool.id}
                serverInfo={{
                  id: configTool.id,
                  mcp_server_name: configTool.mcp_server_name,
                  server_url: configTool.server_url,
                  transport_type: configTool.transport_type,
                  metadata: configTool.metadata,
                  icon: configTool.icon
                }}
                onSuccess={() => {
                  toast({
                    title: 'Connected',
                    description: `${configTool.mcp_server_name} connected successfully via MCP Host`
                  });
                  setConfigTool(null);
                  loadTools(); // Reload to update status
                  checkConnectionStatuses(); // Refresh connection statuses
                }}
                onCancel={() => setConfigTool(null)}
              />
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ExternalTools;